import React, { useEffect, useState, useCallback } from "react";
import {
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";
import { formatDateDDMMYYYY } from "../../utils/dateUtils";
import { showToast } from "../../utils/toastUtils";
import {
  fetchWfhEntries,
  submitWfhEntries,
  deleteWfhEntry,
} from "../../utils/wfhUtils";
import type { WfhEntry } from "../../utils/wfhUtils";
import {
  enumerateDateRange,
  formatNumDays,
} from "../../utils/leaveUtils";
import CustomDropdown from "../CustomDropdown";

// Inline SVG icons for actions
const EditSvg: React.FC<{ size?: number; className?: string }> = ({
  size = 16,
  className = "",
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const TrashSvg: React.FC<{ size?: number; className?: string }> = ({
  size = 16,
  className = "",
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

type Manager = {
  id: string;
  firstName?: string;
  lastName?: string;
  role?: string;
};

interface WfhApplyProps {
  organizationId?: string;
  userId?: string;
  currentUser?: any;
}

const normalizeRole = (raw?: string | null) =>
  (raw || "").toLowerCase().replace(/\s+/g, "");
const isManagerialRole = (raw?: string | null) => {
  const norm = normalizeRole(raw);
  return norm === "manager" || norm === "managingdirector";
};

const WfhApply: React.FC<WfhApplyProps> = ({
  organizationId,
  userId,
  currentUser,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  const [selectedTab, setSelectedTab] = useState<"new" | "rejected" | "history">("new");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Modal state
  const [isModalVisible, setModalVisible] = useState(false);

  // Edit modal state
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editFromDate, setEditFromDate] = useState<string>("");
  const [editToDate, setEditToDate] = useState<string>("");
  const [editReason, setEditReason] = useState("");
  const [editNumDays, setEditNumDays] = useState<string>("");
  const [editSelectedManager, setEditSelectedManager] = useState<string>("");

  const editRangeDates = React.useMemo(
    () => enumerateDateRange(editFromDate, editToDate),
    [editFromDate, editToDate]
  );

  // Form fields
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [reason, setReason] = useState("");
  const [numDays, setNumDays] = useState<string>("");
  const [selectedManager, setSelectedManager] = useState<string>("");

  const [managers, setManagers] = useState<Manager[]>([]);
  const [draftEntries, setDraftEntries] = useState<WfhEntry[]>([]);
  const [rejectedEntries, setRejectedEntries] = useState<WfhEntry[]>([]);
  const [historyEntries, setHistoryEntries] = useState<WfhEntry[]>([]);

  const rangeDates = React.useMemo(
    () => enumerateDateRange(fromDate, toDate),
    [fromDate, toDate]
  );

  useEffect(() => {
    if (rangeDates.length === 0) {
      setNumDays("");
      return;
    }
    setNumDays(String(rangeDates.length));
  }, [rangeDates]);

  useEffect(() => {
    if (editRangeDates.length === 0) {
      setEditNumDays("");
      return;
    }
    setEditNumDays(String(editRangeDates.length));
  }, [editRangeDates]);

  useEffect(() => {
    if (!organizationId || !userId) return;
    const loadManagers = async () => {
      try {
        const orgDocRef = doc(db, `organization/${organizationId}`);
        const orgDocSnap = await getDoc(orgDocRef);
        if (orgDocSnap.exists()) {
          const orgData = orgDocSnap.data() as any;
          const allMembers: Manager[] = orgData.members || [];
          const managerList = allMembers.filter(
            (m) =>
              (isManagerialRole(m.role) || (m as any).approvalStatus === "Yes") &&
              m.id !== userId
          );
          setManagers(managerList);
        }
      } catch {
        // ignore
      }
    };
    loadManagers();
  }, [organizationId, userId]);

  const loadAllEntries = useCallback(async () => {
    if (!organizationId || !userId) return;
    setIsLoading(true);
    try {
      // Read local drafts from tempWfhRequests on org member doc
      const orgDocRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgDocRef);
      let tempWfh: WfhEntry[] = [];
      if (orgSnap.exists()) {
        const orgData = orgSnap.data() as any;
        const members: any[] = orgData.members || [];
        const me = members.find((m: any) => m.id === userId);
        if (me) {
          tempWfh = (me.tempWfhRequests || []) as WfhEntry[];
        }
      }
      const sortedDrafts = [...tempWfh].sort((a, b) => {
        const dateA = new Date(a.fromDate).getTime();
        const dateB = new Date(b.fromDate).getTime();
        if (dateA !== dateB) return dateB - dateA;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
      setDraftEntries(sortedDrafts);

      // Read submitted entries from Firestore
      const entries = await fetchWfhEntries(organizationId, userId);
      const sorted = entries.sort((a, b) => {
        const dateA = new Date(a.fromDate).getTime();
        const dateB = new Date(b.fromDate).getTime();
        if (dateA !== dateB) return dateB - dateA;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });

      setRejectedEntries(sorted.filter((e) => {
        const s = (e.approvalStatus || "").toLowerCase();
        return s === "rejected" || s === "reject";
      }));
      setHistoryEntries(sorted);
    } catch (error) {
      console.error("Error fetching Remote Work entries:", error);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, userId]);

  useEffect(() => {
    loadAllEntries();
  }, [loadAllEntries]);

  const resetForm = () => {
    setFromDate("");
    setToDate("");
    setReason("");
    setNumDays("");
    setSelectedManager("");
  };

  const validateFields = () => {
    if (!fromDate) return "Please choose a From Date.";
    if (!toDate) return "Please choose a To Date.";
    if (!reason.trim()) return "Please enter a reason.";
    if (!selectedManager) return "Please select a manager.";
    const days = Number(numDays);
    if (isNaN(days) || days <= 0) return "Number of days must be greater than zero.";
    const start = new Date(fromDate);
    const end = new Date(toDate);
    if (start > end) return "From Date cannot be after To Date.";
    return "";
  };

  const handleSubmit = async () => {
    if (!organizationId || !userId) {
      showToast({ title: "Missing organization or user", status: "error" });
      return;
    }
    if (draftEntries.length === 0) {
      showToast({ title: "No pending Remote Work requests to submit.", status: "warning" });
      return;
    }
    setIsSaving(true);
    try {
      // Read current tempWfhRequests to ensure we have the latest
      const orgDocRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgDocRef);
      if (!orgSnap.exists()) {
        showToast({ title: "Organization doc not found.", status: "error" });
        return;
      }
      const data = orgSnap.data() as any;
      const members: any[] = data.members || [];
      const idx = members.findIndex((m: any) => m.id === userId);
      if (idx === -1) {
        showToast({ title: "User not found in organization.", status: "error" });
        return;
      }
      const user = members[idx];
      const tempRequestsData: WfhEntry[] = user.tempWfhRequests || [];
      if (tempRequestsData.length === 0) {
        showToast({ title: "No Remote Work requests to submit.", status: "warning" });
        return;
      }

      // Submit all temp requests to WorkFromHomeData collection
      await submitWfhEntries(organizationId, userId, tempRequestsData.map((e) => ({ ...e, userId })));

      // Clear tempWfhRequests
      members[idx] = { ...user, tempWfhRequests: [] };
      await updateDoc(orgDocRef, { members });
      setDraftEntries([]);
      showToast({ title: "Remote Work requests submitted successfully.", status: "success" });
      await loadAllEntries();
    } catch (error) {
      console.error("Error submitting Remote Work requests:", error);
      showToast({ title: "Failed to submit Remote Work requests", status: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAndSubmit = async () => {
    if (!organizationId || !userId) {
      showToast({ title: "Missing organization or user", status: "error" });
      return;
    }
    const err = validateFields();
    if (err) {
      showToast({ title: err, status: "warning" });
      return;
    }
    setIsSaving(true);
    try {
      const newEntry: WfhEntry = {
        fromDate,
        toDate,
        reason: reason.trim(),
        numDays,
        approvalStatus: "Pending",
        timestamp: new Date().toISOString(),
        userId,
        managerIds: [selectedManager],
      };
      const orgDocRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgDocRef);
      if (!orgSnap.exists()) {
        showToast({ title: "Organization doc not found.", status: "error" });
        return;
      }
      const data = orgSnap.data() as any;
      const members: any[] = data.members || [];
      const idx = members.findIndex((m: any) => m.id === userId);
      if (idx === -1) {
        showToast({ title: "User not found in organization.", status: "error" });
        return;
      }
      const user = members[idx];
      const updatedTemp = user.tempWfhRequests
        ? [...user.tempWfhRequests, newEntry]
        : [newEntry];
      const sorted = [...updatedTemp].sort((a: WfhEntry, b: WfhEntry) => {
        const dateA = new Date(a.fromDate).getTime();
        const dateB = new Date(b.fromDate).getTime();
        if (dateA !== dateB) return dateB - dateA;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
      members[idx] = { ...user, tempWfhRequests: sorted };
      await updateDoc(orgDocRef, { members });
      setDraftEntries(sorted);
      showToast({ title: "Remote Work request added to draft", status: "success" });
      resetForm();
      setModalVisible(false);
    } catch (error) {
      console.error("Error adding Remote Work request:", error);
      showToast({ title: "Failed to add Remote Work request", status: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (entry: WfhEntry, isDraft: boolean = false) => {
    if (!organizationId || !userId) return;
    if (!window.confirm("Delete this Remote Work request?")) return;
    try {
      if (isDraft) {
        // Delete from tempWfhRequests on org member doc
        const orgDocRef = doc(db, `organization/${organizationId}`);
        const orgSnap = await getDoc(orgDocRef);
        if (!orgSnap.exists()) return;
        const data = orgSnap.data() as any;
        const members: any[] = data.members || [];
        const idx = members.findIndex((m: any) => m.id === userId);
        if (idx === -1) return;
        const user = members[idx];
        const updatedTemp = [...(user.tempWfhRequests || [])];
        const entryIdx = updatedTemp.findIndex(
          (e: WfhEntry) => e.timestamp === entry.timestamp && e.fromDate === entry.fromDate
        );
        if (entryIdx !== -1) {
          updatedTemp.splice(entryIdx, 1);
          members[idx] = { ...user, tempWfhRequests: updatedTemp };
          await updateDoc(orgDocRef, { members });
          setDraftEntries(updatedTemp);
        }
      } else {
        await deleteWfhEntry(organizationId, userId, entry);
        await loadAllEntries();
      }
      showToast({ title: "Remote Work entry deleted", status: "success" });
    } catch {
      showToast({ title: "Failed to delete Remote Work entry", status: "error" });
    }
  };

  const handleEditOpen = (entry: WfhEntry, index: number) => {
    setEditIndex(index);
    setEditFromDate(entry.fromDate || "");
    setEditToDate(entry.toDate || "");
    setEditReason(entry.reason || "");
    setEditNumDays(entry.numDays || "");
    setEditSelectedManager(
      entry.managerIds && entry.managerIds.length > 0 ? entry.managerIds[0] : ""
    );
    setEditModalVisible(true);
  };

  const handleSaveEditChanges = async () => {
    if (!organizationId || !userId || editIndex === null) return;
    if (!editFromDate) {
      showToast({ title: "Please choose a From Date.", status: "warning" });
      return;
    }
    if (!editToDate) {
      showToast({ title: "Please choose a To Date.", status: "warning" });
      return;
    }
    if (!editReason.trim()) {
      showToast({ title: "Please enter a reason.", status: "warning" });
      return;
    }
    if (!editSelectedManager) {
      showToast({ title: "Please select a manager.", status: "warning" });
      return;
    }
    const days = Number(editNumDays);
    if (isNaN(days) || days <= 0) {
      showToast({ title: "Number of days must be greater than zero.", status: "warning" });
      return;
    }
    const start = new Date(editFromDate);
    const end = new Date(editToDate);
    if (start > end) {
      showToast({ title: "From Date cannot be after To Date.", status: "warning" });
      return;
    }

    setIsEditSaving(true);
    try {
      const updatedEntry: WfhEntry = {
        fromDate: editFromDate,
        toDate: editToDate,
        reason: editReason.trim(),
        numDays: editNumDays,
        approvalStatus: "Pending",
        timestamp: new Date().toISOString(),
        userId,
        managerIds: [editSelectedManager],
      };
      const orgDocRef = doc(db, `organization/${organizationId}`);
      const orgSnap = await getDoc(orgDocRef);
      if (!orgSnap.exists()) {
        showToast({ title: "Organization doc not found.", status: "error" });
        return;
      }
      const data = orgSnap.data() as any;
      const members: any[] = data.members || [];
      const idx = members.findIndex((m: any) => m.id === userId);
      if (idx === -1) {
        showToast({ title: "User not found in organization.", status: "error" });
        return;
      }
      const user = members[idx];
      const updatedTemp = [...(user.tempWfhRequests || [])];
      updatedTemp[editIndex] = updatedEntry;
      const sorted = [...updatedTemp].sort((a: WfhEntry, b: WfhEntry) => {
        const dateA = new Date(a.fromDate).getTime();
        const dateB = new Date(b.fromDate).getTime();
        if (dateA !== dateB) return dateB - dateA;
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      });
      members[idx] = { ...user, tempWfhRequests: sorted };
      await updateDoc(orgDocRef, { members });
      setDraftEntries(sorted);
      setEditModalVisible(false);
      showToast({ title: "Remote Work request updated", status: "success" });
    } catch {
      showToast({ title: "Failed to edit Remote Work request.", status: "error" });
    } finally {
      setIsEditSaving(false);
    }
  };

  const getManagerName = (mgrId?: string) => {
    if (!mgrId) return "-";
    const m = managers.find((mm) => mm.id === mgrId);
    return m ? `${m.firstName || ""} ${m.lastName || ""}`.trim() : mgrId;
  };

  const getStatusBadge = (status: string) => {
    const s = (status || "").toLowerCase();
    if (s === "approved") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 border border-green-200">
          Approved
        </span>
      );
    }
    if (s === "rejected" || s === "reject") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">
          Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-100 text-yellow-700 border border-yellow-200">
        Pending
      </span>
    );
  };

  const openModal = () => {
    resetForm();
    setModalVisible(true);
  };

  return (
    <div className={`flex flex-col h-full ${isDark ? theme.text : "text-gray-900"}`}>
      {/* Header row */}
      <div className="flex flex-row items-end justify-between mb-6">
        <div className="flex-1 flex items-center gap-2">
          <h2 className={`text-xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Remote Work
          </h2>
        </div>
        <div className="w-full max-w-[200px] flex justify-end">
          <CustomDropdown
            value={selectedTab}
            onChange={(val: any) => setSelectedTab(val)}
            isDark={isDark}
            className="h-10 text-sm font-semibold rounded-md border-[#B59143]/40 w-full"
            options={[
              { value: "new", label: "New" },
              { value: "rejected", label: "Rejected" },
              { value: "history", label: "History" },
            ]}
          />
        </div>
      </div>

      {/* New tab (Drafts / Pending) */}
      {selectedTab === "new" && (
        <>
          {!draftEntries || draftEntries.length === 0 ? (
            <div className={`flex flex-col items-center justify-center py-12 border border-dashed rounded-lg ${theme.card} ${theme.border}`}>
              <p className={`${theme.textMuted} mb-4`}>No Remote Work requests added yet.</p>
              <button
                onClick={openModal}
                className={`px-4 py-2 rounded-full text-sm focus:outline-none ${theme.primaryButton}`}
              >
                Add Remote Work
              </button>
            </div>
          ) : (
            <div className={`max-h-[400px] overflow-y-auto overflow-x-auto ${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} w-full border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'} rounded-md mb-4`}>
              <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      S.No
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      From
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      To
                    </th>
                    <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Days
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Reason
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Manager
                    </th>
                    <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? 'bg-[#1A1C1F] divide-y divide-[#2E2F33]' : 'bg-white divide-y divide-gray-200'}`}>
                  {draftEntries.map((item, idx) => (
                    <tr
                      key={`${item.timestamp}-${idx}`}
                      className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'}`}
                    >
                      <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {idx + 1}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatDateDDMMYYYY(item.fromDate)}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatDateDDMMYYYY(item.toDate)}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.numDays ? formatNumDays(item.numDays) : "-"}
                      </td>
                      <td className={`px-2 py-2 text-sm truncate max-w-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.reason}
                      </td>
                      <td className={`px-2 py-2 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {Array.isArray(item.managerIds) && item.managerIds.length > 0
                          ? item.managerIds.map((mid) => getManagerName(mid)).join(", ")
                          : "-"}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => handleEditOpen(item, idx)}
                            className={`inline-flex items-center p-1 rounded-full text-[#B59143] ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'} focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:ring-opacity-50`}
                            title="Edit"
                          >
                            <EditSvg />
                          </button>
                          <button
                            onClick={() => handleDelete(item, true)}
                            className={`inline-flex items-center p-1 rounded-full text-rose-600 ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-rose-100'} focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-opacity-50`}
                            title="Delete"
                          >
                            <TrashSvg />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-4 justify-end mt-4">
            <button
              onClick={openModal}
              className={`px-4 py-2 rounded-full text-sm focus:outline-none ${theme.primaryButton}`}
            >
              Add +
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving || draftEntries.length === 0}
              className={`px-4 py-2 text-white rounded-full text-sm focus:outline-none ${isSaving || draftEntries.length === 0 ? 'opacity-60 cursor-not-allowed' : ''
                } ${theme.primaryButton}`}
            >
              {isSaving ? "Saving..." : "Submit"}
            </button>
          </div>
        </>
      )}

      {/* Rejected tab */}
      {selectedTab === "rejected" && (
        <div>
          {rejectedEntries.length === 0 ? (
            <div className={`flex flex-col items-center justify-center py-12 border border-dashed rounded-lg ${theme.card} ${theme.border}`}>
              <p className={`${theme.textMuted}`}>No rejected Remote Work requests.</p>
            </div>
          ) : (
            <div className={`max-h-[400px] overflow-y-auto overflow-x-auto ${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} w-full border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'} rounded-md`}>
              <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      S.No
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      From
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      To
                    </th>
                    <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Days
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Reason
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Manager
                    </th>
                    <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? 'bg-[#1A1C1F] divide-y divide-[#2E2F33]' : 'bg-white divide-y divide-gray-200'}`}>
                  {rejectedEntries.map((item, idx) => (
                    <tr
                      key={`${item.timestamp}-${idx}`}
                      className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'}`}
                    >
                      <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {idx + 1}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatDateDDMMYYYY(item.fromDate)}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatDateDDMMYYYY(item.toDate)}
                      </td>
                      <td className={`px-2 py-2 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.numDays ? formatNumDays(item.numDays) : "-"}
                      </td>
                      <td className={`px-2 py-2 text-sm truncate max-w-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.reason}
                      </td>
                      <td className={`px-2 py-2 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {Array.isArray(item.managerIds) && item.managerIds.length > 0
                          ? item.managerIds.map((mid) => getManagerName(mid)).join(", ")
                          : "-"}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(item)}
                            className={`inline-flex items-center p-1 rounded-full text-rose-600 ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-rose-100'} focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-opacity-50`}
                            title="Delete"
                          >
                            <TrashSvg />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* History tab */}
      {selectedTab === "history" && (
        <div>
          {isLoading ? (
            <div className={`flex flex-col items-center justify-center py-12 border border-dashed rounded-lg ${theme.card} ${theme.border}`}>
              <p className={`${theme.textMuted}`}>Loading...</p>
            </div>
          ) : historyEntries.length === 0 ? (
            <div className={`flex flex-col items-center justify-center py-12 border border-dashed rounded-lg ${theme.card} ${theme.border}`}>
              <p className={`${theme.textMuted}`}>No Remote Work requests found.</p>
            </div>
          ) : (
            <div className={`max-h-[400px] overflow-y-auto overflow-x-auto ${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} w-full border ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'} rounded-md`}>
              <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      S.No
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      From
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      To
                    </th>
                    <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Days
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Reason
                    </th>
                    <th className={`px-2 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Manager
                    </th>
                    <th className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-600'}`}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? 'bg-[#1A1C1F] divide-y divide-[#2E2F33]' : 'bg-white divide-y divide-gray-200'}`}>
                  {historyEntries.map((item, idx) => {
                    return (
                      <tr
                        key={`${item.timestamp}-${idx}`}
                        className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'}`}
                      >
                        <td className={`px-2 py-2 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {idx + 1}
                        </td>
                        <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatDateDDMMYYYY(item.fromDate)}
                        </td>
                        <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatDateDDMMYYYY(item.toDate)}
                        </td>
                        <td className={`px-2 py-2 whitespace-nowrap text-sm text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {item.numDays ? formatNumDays(item.numDays) : "-"}
                        </td>
                        <td className={`px-2 py-2 text-sm truncate max-w-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {item.reason}
                        </td>
                        <td className={`px-2 py-2 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {Array.isArray(item.managerIds) && item.managerIds.length > 0
                            ? item.managerIds.map((mid) => getManagerName(mid)).join(", ")
                            : "-"}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {getStatusBadge(item.approvalStatus)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Remote Work Modal */}
      {isModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0B0B0F]/60 backdrop-blur-md animate-in fade-in duration-300">
          <div
            role="dialog"
            aria-modal="true"
            className={`relative w-full max-w-2xl ${isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white border-gray-200'
              } rounded-[28px] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.25)] border overflow-hidden transform transition-all animate-in zoom-in-95 duration-300`}
          >
            {/* Top accent gradient line */}
            <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] opacity-90" />

            <div className="p-8 sm:p-10">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <h3 className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Add Remote Work Request
                  </h3>
                  <p className={`text-sm mt-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Fill the details to add a remote work request.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setModalVisible(false);
                    resetForm();
                  }}
                  className={`inline-flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-200 ${isDark
                    ? 'border-[#2E2F33] text-gray-400 hover:bg-[#2A3441] hover:text-white'
                    : 'border-gray-100 text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                    } focus:outline-none`}
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* From Date */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      if (!toDate || new Date(e.target.value) > new Date(toDate)) {
                        setToDate(e.target.value);
                      }
                    }}
                    className={`w-full rounded-xl px-4 py-2.5 text-sm transition-all border shadow-sm outline-none focus:ring-2 focus:ring-[#B59143] ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33]'
                      : 'bg-gray-50 text-gray-900 border-gray-200'
                      }`}
                  />
                </div>

                {/* To Date */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    min={fromDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={`w-full rounded-xl px-4 py-2.5 text-sm transition-all border shadow-sm outline-none focus:ring-2 focus:ring-[#B59143] ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33]'
                      : 'bg-gray-50 text-gray-900 border-gray-200'
                      }`}
                  />
                </div>

                {/* Reason */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Reason
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason for Remote Work"
                    className={`w-full rounded-xl px-4 py-2.5 text-sm transition-all border shadow-sm outline-none focus:ring-2 focus:ring-[#B59143] ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33] placeholder-gray-600'
                      : 'bg-gray-50 text-gray-900 border-gray-200 placeholder-gray-400'
                      }`}
                  />
                </div>

                {/* Manager */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Manager
                  </label>
                  <CustomDropdown
                    value={selectedManager}
                    onChange={(value) => setSelectedManager(value)}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Manager" },
                      ...managers.map((mgr) => ({
                        value: mgr.id,
                        label: `${mgr.firstName || ""} ${mgr.lastName || ""}`.trim()
                      }))
                    ]}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-4 mt-10">
                <button
                  onClick={() => {
                    setModalVisible(false);
                    resetForm();
                  }}
                  className={`px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 border ${isDark
                    ? 'border-[#2E2F33] text-gray-300 hover:bg-[#2A3441] hover:text-white'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAndSubmit}
                  disabled={isSaving}
                  className={`px-8 py-2.5 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-[0.98] ${isSaving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#E8C16C] to-[#B59143] hover:shadow-[0_8px_20px_-6px_rgba(181,145,67,0.5)]'
                    }`}
                >
                  {isSaving ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </div>
                  ) : (
                    "Submit Request"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Remote Work Modal */}
      {isEditModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0B0B0F]/60 backdrop-blur-md animate-in fade-in duration-300">
          <div
            role="dialog"
            aria-modal="true"
            className={`relative w-full max-w-2xl ${isDark ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white border-gray-200'
              } rounded-[28px] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.25)] border overflow-hidden transform transition-all animate-in zoom-in-95 duration-300`}
          >
            {/* Top accent gradient line */}
            <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#FBD282] via-[#E8C16C] to-[#B59143] opacity-90" />

            <div className="p-8 sm:p-10">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <h3 className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Edit Remote Work Request
                  </h3>
                  <p className={`text-sm mt-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Update the remote work request details.
                  </p>
                </div>
                <button
                  onClick={() => setEditModalVisible(false)}
                  className={`inline-flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-200 ${isDark
                    ? 'border-[#2E2F33] text-gray-400 hover:bg-[#2A3441] hover:text-white'
                    : 'border-gray-100 text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                    } focus:outline-none`}
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* From Date */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={editFromDate}
                    onChange={(e) => {
                      setEditFromDate(e.target.value);
                      if (!editToDate || new Date(e.target.value) > new Date(editToDate)) {
                        setEditToDate(e.target.value);
                      }
                    }}
                    className={`w-full rounded-xl px-4 py-2.5 text-sm transition-all border shadow-sm outline-none focus:ring-2 focus:ring-[#B59143] ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33]'
                      : 'bg-gray-50 text-gray-900 border-gray-200'
                      }`}
                  />
                </div>

                {/* To Date */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={editToDate}
                    min={editFromDate}
                    onChange={(e) => setEditToDate(e.target.value)}
                    className={`w-full rounded-xl px-4 py-2.5 text-sm transition-all border shadow-sm outline-none focus:ring-2 focus:ring-[#B59143] ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33]'
                      : 'bg-gray-50 text-gray-900 border-gray-200'
                      }`}
                  />
                </div>

                {/* Reason */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Reason
                  </label>
                  <input
                    type="text"
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    placeholder="Enter reason for Remote Work"
                    className={`w-full rounded-xl px-4 py-2.5 text-sm transition-all border shadow-sm outline-none focus:ring-2 focus:ring-[#B59143] ${isDark
                      ? 'bg-[#0B0B0F] text-white border-[#2E2F33] placeholder-gray-600'
                      : 'bg-gray-50 text-gray-900 border-gray-200 placeholder-gray-400'
                      }`}
                  />
                </div>

                {/* Manager */}
                <div className="space-y-1.5">
                  <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Manager
                  </label>
                  <CustomDropdown
                    value={editSelectedManager}
                    onChange={(value) => setEditSelectedManager(value)}
                    isDark={isDark}
                    className="w-full"
                    options={[
                      { value: "", label: "Select Manager" },
                      ...managers.map((mgr) => ({
                        value: mgr.id,
                        label: `${mgr.firstName || ""} ${mgr.lastName || ""}`.trim()
                      }))
                    ]}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-4 mt-10">
                <button
                  onClick={() => setEditModalVisible(false)}
                  className={`px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 border ${isDark
                    ? 'border-[#2E2F33] text-gray-300 hover:bg-[#2A3441] hover:text-white'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEditChanges}
                  disabled={isEditSaving}
                  className={`px-8 py-2.5 rounded-xl font-bold text-white shadow-lg transition-all transform active:scale-[0.98] ${isEditSaving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#E8C16C] to-[#B59143] hover:shadow-[0_8px_20px_-6px_rgba(181,145,67,0.5)]'
                    }`}
                >
                  {isEditSaving ? (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </div>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WfhApply;
